import React from 'react';
import {
  Box, Typography, Stack, Chip, Divider, Button, Paper, Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  LocalHospital as MedicalIcon,
  CalendarToday as CalendarIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

function AppointmentDetail({ appointment, onEdit }) {
  if (!appointment) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4,
      }}
      >
        <EventIcon sx={{ fontSize: 100, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Select an appointment to view details
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'Confirmed': return 'success';
      case 'In Progress': return 'info';
      case 'Completed': return 'default';
      case 'Cancelled': return 'error';
      case 'No Show': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <EventIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {appointment.patientName || 'Unknown Patient'}
              </Typography>
              <Chip
                label={appointment.status}
                size="small"
                color={getStatusColor(appointment.status)}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit(appointment)}
          >
            Edit
          </Button>
        </Stack>

        <Divider />

        {/* Appointment Info */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Date & Time
                  </Typography>
                </Stack>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Date:</strong>
                    {' '}
                    {format(parseISO(appointment.date), 'EEEE, MMMM dd, yyyy')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Time:</strong>
                    {' '}
                    {appointment.startTime}
                    {' '}
                    -
                    {' '}
                    {appointment.endTime}
                  </Typography>
                  {appointment.duration && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Duration:
                      {' '}
                      {appointment.duration}
                      {' '}
                      minutes
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MedicalIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Appointment Details
                  </Typography>
                </Stack>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Type:</strong>
                    {' '}
                    {appointment.appointmentType}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Provider:</strong>
                    {' '}
                    {appointment.provider}
                  </Typography>
                  {appointment.location && (
                    <Typography variant="body2">
                      <strong>Location:</strong>
                      {' '}
                      {appointment.location}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Patient Information
                  </Typography>
                </Stack>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Name:</strong>
                    {' '}
                    {appointment.patientName || 'Not specified'}
                  </Typography>
                  {appointment.patientId && (
                    <Typography variant="body2">
                      <strong>Patient ID:</strong>
                      {' '}
                      {appointment.patientId}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {appointment.reason && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <NotesIcon color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Reason for Visit
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ pl: 4, whiteSpace: 'pre-wrap' }}>
                    {appointment.reason}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          )}

          {appointment.notes && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <NotesIcon color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Notes
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ pl: 4, whiteSpace: 'pre-wrap' }}>
                    {appointment.notes}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Metadata */}
        <Divider />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Created:
            {' '}
            {format(parseISO(appointment.createdAt || new Date().toISOString()), 'PPpp')}
          </Typography>
          {appointment.updatedAt && appointment.updatedAt !== appointment.createdAt && (
            <Typography variant="caption" color="text.secondary" display="block">
              Updated:
              {' '}
              {format(parseISO(appointment.updatedAt), 'PPpp')}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default AppointmentDetail;
